import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { mdiChevronDown, mdiChevronRight, mdiPencil } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { Link } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '../ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog'
import { cn } from '../../lib/utils'
import { formatCost, COMPLIANCE_COLORS } from '../../lib/capex-utils'
import { $api } from '../../api/queryClient'
import { useDeleteDocument } from '../../hooks/useQueries'
import type { WorkPackage, ComplianceLevel } from './types'
import { useCapexCurrency } from './CapexCurrencyContext'
import { SECTION_ICONS } from '../../lib/section-icons'

interface PackageRowProps {
  pkg: WorkPackage
  worksiteYear: number
  horizonStart: number
  horizonEnd: number
  org: string
  complianceLevel?: ComplianceLevel | null
  isReadOnly?: boolean
  onQuote?: (pkg: WorkPackage) => void
  onDeleteQuote?: (pkg: WorkPackage) => void
  onEditPackage?: (pkg: WorkPackage) => void
}

function packageCostDisplay(pkg: WorkPackage, currency?: string | null): string {
  if (pkg.quote_amount != null) {
    return formatCost(pkg.quote_amount, currency)
  }
  const low = pkg.cost_low ?? 0
  const high = pkg.cost_high ?? 0
  if (low === high) return formatCost(low, currency)
  const lowStr = low >= 1000 ? `${Math.round(low / 1000)}k` : `${Math.round(low)}`
  return `${lowStr}–${formatCost(high, currency)}`
}

export const PackageRow = React.memo(function PackageRow({
  pkg,
  worksiteYear,
  horizonStart,
  horizonEnd,
  org,
  complianceLevel: level,
  isReadOnly,
  onQuote,
  onDeleteQuote,
  onEditPackage,
}: PackageRowProps) {
  const capexCurrency = useCapexCurrency()
  const { t } = useTranslation('capex')
  const queryClient = useQueryClient()

  const [isOpen, setIsOpen] = useState(false)
  const [docToDelete, setDocToDelete] = useState<{ uid: string; name: string } | null>(null)

  const years: number[] = []
  for (let y = horizonStart; y <= horizonEnd; y++) {
    years.push(y)
  }

  // total columns = 1 (label) + number of years
  const totalColumns = 1 + years.length

  const complianceBg = level ? COMPLIANCE_COLORS[level].bg : ''
  const hasQuote = pkg.quote_amount != null

  // Fetch documents linked to this work package, only when expanded
  const { data: documentsData, isLoading: docsLoading } = $api.useQuery(
    'get',
    '/api/v1/organizations/{orgCode}/documents',
    {
      params: {
        path: { orgCode: org },
        query: {
          linked_entity_type: 'work_package' as any,
          linked_entity_uid: pkg.uid,
        },
      },
    },
    { enabled: isOpen, staleTime: 5 * 60 * 1000, retry: 1 },
  )

  const documents: Array<{ uid: string; name: string; file_name?: string }> =
    (documentsData as any)?.data ?? (Array.isArray(documentsData) ? documentsData : [])

  const deleteDocument = useDeleteDocument(org)

  const handleDeleteConfirm = () => {
    if (!docToDelete) return
    deleteDocument.mutate(docToDelete.uid, {
      onSuccess: () => {
        // Invalidate the work package documents query
        queryClient.invalidateQueries({
          predicate: (query) => {
            const queryKey = query.queryKey as any[]
            if (queryKey.length >= 2 && queryKey[0] === 'get') {
              const path = queryKey[1]
              if (path === '/api/v1/organizations/{orgCode}/documents') {
                const params = queryKey.find(
                  (item) => item && typeof item === 'object' && 'params' in item,
                )?.params
                if (
                  params?.path?.orgCode === org &&
                  params?.query?.linked_entity_type === 'work_package' &&
                  params?.query?.linked_entity_uid === pkg.uid
                ) {
                  return true
                }
              }
            }
            return false
          },
        })
        setDocToDelete(null)
      },
    })
  }

  return (
    <>
      <tr
        className={cn(
          'group bg-muted/20 hover:bg-muted/30 border-b border-border/50',
          complianceBg,
        )}
        data-testid={`package-row-${pkg.uid}`}
      >
        {/* Label cell — sticky left */}
        <td
          className={cn(
            'sticky left-0 z-10 border-r border-border/50 px-3 py-1.5 w-80 min-w-80',
            complianceBg || 'bg-muted/20',
          )}
        >
          <div className="flex items-center gap-2 pl-6">
            <button
              type="button"
              className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
              onClick={() => setIsOpen((v) => !v)}
              title={isOpen ? t('package_documents.toggle_collapse') : t('package_documents.toggle_expand')}
              data-testid={`package-docs-toggle-${pkg.uid}`}
              aria-expanded={isOpen}
            >
              {isOpen ? (
                <AppIcon path={mdiChevronDown} size="xs" tone="muted" />
              ) : (
                <AppIcon path={mdiChevronRight} size="xs" tone="muted" />
              )}
            </button>
            <span className="text-xs text-muted-foreground truncate">{pkg.label}</span>
            {level && (
              <span
                className={cn(
                  'text-xs px-1.5 py-0.5 rounded-full font-medium shrink-0',
                  COMPLIANCE_COLORS[level].badge,
                )}
                aria-label={`Compliance: ${t(`compliance.${level}`)}`}
                data-testid={`package-compliance-badge-${pkg.uid}`}
              >
                {t(`compliance.${level}`)}
              </span>
            )}
            {!level && pkg.compliance_evaluation_uid && (
              <span className="text-xs text-amber-600 shrink-0">&#9888;</span>
            )}
            {!isReadOnly && (
              <Button
                variant="ghost"
                size="sm"
                className="h-5 w-5 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={() => onEditPackage?.(pkg)}
                title="Edit work package"
                data-testid={`package-action-edit-${pkg.uid}`}
              >
                <AppIcon path={mdiPencil} size="xs" />
              </Button>
            )}
          </div>
        </td>

        {/* Year cells */}
        {years.map((year) => (
          <td
            key={year}
            className="border-r border-border/30 px-2 py-1.5 text-center align-middle"
          >
            {year === worksiteYear ? (
              <div className="flex flex-col items-center gap-0.5">
                <span
                  className={cn(
                    'text-xs font-medium',
                    hasQuote ? 'text-emerald-600' : 'text-foreground',
                  )}
                >
                  {packageCostDisplay(pkg, capexCurrency)}
                </span>
                {!isReadOnly && (
                  hasQuote ? (
                    <div className="flex items-center gap-1">
                      {(pkg as any).quote_document_uid ? (
                        <Link
                          to="/$org/documents/$docUid"
                          params={{ org, docUid: (pkg as any).quote_document_uid }}
                          target="_blank"
                          className="text-xs text-emerald-600 hover:underline"
                          data-testid={`package-quote-doc-link-${pkg.uid}`}
                        >
                          {t('quote.quoted')}
                        </Link>
                      ) : (
                        <span className="text-xs text-emerald-600">{t('quote.quoted')}</span>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-5 w-5 p-0 text-muted-foreground hover:text-foreground"
                        onClick={() => onQuote?.(pkg)}
                        data-testid={`package-quote-edit-${pkg.uid}`}
                        title={t('quote.edit_title', 'Edit quote')}
                      >
                        <AppIcon path={mdiPencil} size="xs" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-5 px-1 text-xs text-muted-foreground hover:text-destructive"
                        onClick={() => onDeleteQuote?.(pkg)}
                        data-testid={`package-quote-delete-${pkg.uid}`}
                        title={t('quote.delete_title')}
                      >
                        &times;
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-5 px-1.5 text-xs text-muted-foreground hover:text-foreground"
                      onClick={() => onQuote?.(pkg)}
                      data-testid={`package-quote-btn-${pkg.uid}`}
                      title={t('quote.dialog_title')}
                    >
                      {t('quote.dialog_title')}
                    </Button>
                  )
                )}
                {isReadOnly && hasQuote && (
                  (pkg as any).quote_document_uid ? (
                    <Link
                      to="/$org/documents/$docUid"
                      params={{ org, docUid: (pkg as any).quote_document_uid }}
                      target="_blank"
                      className="text-xs text-emerald-600 hover:underline"
                      data-testid={`package-quote-doc-link-${pkg.uid}`}
                    >
                      {t('quote.quoted')}
                    </Link>
                  ) : (
                    <span className="text-xs text-emerald-600">{t('quote.quoted')}</span>
                  )
                )}
              </div>
            ) : null}
          </td>
        ))}
      </tr>

      {/* Expanded documents row */}
      {isOpen && (
        <tr
          className="bg-muted/10 border-b border-border/30"
          data-testid={`package-docs-row-${pkg.uid}`}
        >
          <td colSpan={totalColumns} className="px-4 py-2 pl-14">
            {docsLoading ? (
              <span className="text-xs text-muted-foreground">{t('package_documents.loading')}</span>
            ) : documents.length === 0 ? (
              <span className="text-xs text-muted-foreground">{t('package_documents.empty')}</span>
            ) : (
              <ul className="flex flex-col gap-1">
                {documents.map((doc) => (
                  <li key={doc.uid} className="flex items-center gap-2">
                    <AppIcon path={SECTION_ICONS.documents} size="xs" className="shrink-0" />
                    <Link
                      to="/$org/documents/$docUid"
                      params={{ org, docUid: doc.uid }}
                      className="text-xs hover:underline text-foreground truncate"
                      data-testid={`package-doc-link-${doc.uid}`}
                    >
                      {doc.name || doc.file_name || doc.uid}
                    </Link>
                    {!isReadOnly && (
                      <button
                        type="button"
                        className="text-xs text-muted-foreground hover:text-destructive transition-colors ml-auto shrink-0"
                        onClick={() => setDocToDelete({ uid: doc.uid, name: doc.name || doc.file_name || doc.uid })}
                        title={t('package_documents.delete_title')}
                        data-testid={`package-doc-delete-${doc.uid}`}
                      >
                        &times;
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </td>
        </tr>
      )}

      {/* Delete document confirmation dialog */}
      <AlertDialog open={docToDelete !== null} onOpenChange={(open) => { if (!open) setDocToDelete(null) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('package_documents.delete_title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('package_documents.delete_description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="package-doc-delete-cancel">
              {t('package_documents.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="package-doc-delete-confirm"
            >
              {t('package_documents.delete_confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
})
